import React, { useCallback, useContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import StoriesListTable from './sections/StoriesListTable/StoriesListTable';
import StoriesTableFilter from './sections/StoriesTableFilter/StoriesTableFilter';
import { BreadcrumbItemProps } from '../../../shared/components/Breadcrumbs/BreadcrumbItem/BreadcrumbItem';
import Breadcrumbs from '../../../shared/components/Breadcrumbs/Breadcrumbs';
import PageCurvedContainer from '../../../shared/components/PageCurvedContainer/PageCurvedContainer';
import PageHeader from '../../../shared/components/PageHeader/PageHeader';
import ToastNE from '../../../shared/components/ToastNE/ToastNE';
import Header from '../../../shared/components/v3/Globals/Header/Header';
import { NEW_STORY_ROUTE } from '../../../shared/constants/auth';
import { DEFAULT_PAGINATOR_STATE_V3, DEFAULT_STORIES_FILTER, ORDER_DESC } from '../../../shared/constants/stories';
import { CLICK_ACTION, NEW_STORY } from '../../../shared/constants/tagManager';
import { CompanyContext } from '../../../shared/context/company/companyContext';
import { StoriesContext } from '../../../shared/context/stories/storiesContext';
import { useIsMounted } from '../../../shared/hooks/useIsMounted';
import { IStoriesFilters, IStoryListData } from '../../../shared/interfaces/stories';
import Stories from '../../../shared/services/api/communication/stories';
import TagManagerService from '../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_STORIES_LIST } from '../../../shared/translations/stories';
import './StoriesList.scss';

const StoriesList = () => {
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [paginatorState, setPaginatorState] = useState<number>(DEFAULT_PAGINATOR_STATE_V3);
  const [actualPage, setActualPage] = useState<number>(0);
  const [stories, setStories] = useState<IStoryListData[] | undefined>(undefined);
  const [totalStories, setTotalStories] = useState<number>(0);
  const [storiesFilters, setStoriesFilters] = useState<IStoriesFilters>(DEFAULT_STORIES_FILTER);
  const [storiesOrder, setStoriesOrder] = useState<string>(ORDER_DESC);
  const [isToastOpen, setIsToastOpen] = useState<boolean>(false);

  const isMounted = useIsMounted();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const { company } = useContext(CompanyContext);
  const { toastData, changeOpenToast, openToast, changeStoryDuplication } = useContext(StoriesContext);

  const breadcrumbs: BreadcrumbItemProps[] = [{ label: t(TRANSLATION_STORIES_LIST.header.pagename) }];

  const goToNewStory = () => {
    navigate(NEW_STORY_ROUTE);
    TagManagerService.pushEvent(CLICK_ACTION, NEW_STORY);
  };
  const changeActualPage = useCallback((newPage: number) => {
    setActualPage(newPage);
  }, []);

  const changePaginatorState = (newPaginatorState: number) => {
    setPaginatorState(newPaginatorState);
    getStoriesList(0, newPaginatorState, storiesFilters, storiesOrder);
  };

  const changeOrder = (order: string) => {
    setStoriesOrder(order);
    getStoriesList(0, paginatorState, storiesFilters, order);
  };

  const changeStoriesFilters = (newFilters: IStoriesFilters) => {
    setStoriesFilters(newFilters);

    getStoriesList(0, paginatorState, newFilters, storiesOrder);
  };

  const isValidCompany = useCallback(() => {
    return company.id !== '';
  }, [company]);

  const getStoriesList = async (page: number, paginator: number, filter: IStoriesFilters, order: string) => {
    if (isMounted && isValidCompany()) {
      try {
        setLoading(true);
        setActualPage(page);
        setStories(undefined);
        setError(false);
        const response = await Stories.getStoriesList(company.id, page + 1, paginator, filter, order);
        if (response.stories.length !== 0) {
          setStories(response.stories);
        }
        setTotalStories(response.totalStories);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseToast = (_event: React.SyntheticEvent | React.MouseEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsToastOpen(false);
  };

  useEffect(() => {
    if (!loading) {
      getStoriesList(actualPage, paginatorState, storiesFilters, storiesOrder);
    }
  }, [actualPage]);

  useEffect(() => {
    setStoriesFilters(DEFAULT_STORIES_FILTER);
    setActualPage(0);
    setStoriesOrder(ORDER_DESC);
    getStoriesList(0, paginatorState, DEFAULT_STORIES_FILTER, ORDER_DESC);
  }, [company, isMounted]);

  useEffect(() => {
    changeStoryDuplication(undefined);
    if (openToast) {
      setIsToastOpen(true);
      changeOpenToast(false);
    }
  }, []);

  return (
    <div className="page-container curved" data-experience="new">
      <Header />
      <div className="page-content-container">
        <Breadcrumbs items={breadcrumbs} />
        <PageHeader
          title={t(TRANSLATION_STORIES_LIST.header.title)}
          description={t(TRANSLATION_STORIES_LIST.header.description)}
          buttonAction={goToNewStory}
          buttonText={t(TRANSLATION_STORIES_LIST.header.button)}
        />
      </div>
      <PageCurvedContainer>
        <h2 className="ne-font-heading-md m-0">{t(TRANSLATION_STORIES_LIST.pageTitle)}</h2>
        <StoriesTableFilter
          company={company}
          loading={loading}
          storiesFilters={storiesFilters}
          changeStoriesFilters={changeStoriesFilters}
        />
        <StoriesListTable
          storiesListData={stories}
          loading={loading}
          error={error}
          totalStories={totalStories}
          paginatorData={{ paginatorState, changePaginatorState, actualPage, changeActualPage }}
          order={{ orderBy: storiesOrder, changeOrder: changeOrder }}
        />
      </PageCurvedContainer>
      <ToastNE
        title={t(toastData.title)}
        description={t(toastData.description)}
        icon={toastData.icon}
        open={isToastOpen}
        button={toastData.button}
        buttonText={t(toastData.buttonText)}
        buttonAction={handleCloseToast}
        handleClose={handleCloseToast}
      />
    </div>
  );
};

export default StoriesList;
